/**
 * 직업. 수치는 `jobs.json`. 타이틀 생성은 `JOB_ORDER`(1차)만 쓴다.
 */

import seed from './jobs.json';
import { emitDataReloadSoon, replaceArray, replaceRecord, requireRecord } from './catalog';
import type { PrimaryStat } from './balance';

export type JobId = string;
export type BasicAttackStyle = 'melee' | 'bolt' | 'arrow' | 'star';
export type JobRank = 1 | 2 | 3 | 4;

export interface JobApStart {
  str: number;
  dex: number;
  int: number;
  luk: number;
}

export interface JobDef {
  id: string;
  name: string;
  nameEn: string;
  rank: JobRank;
  line: string;
  prevId?: string;
  nextIds: string[];
  attackStat: PrimaryStat;
  weaponId: string;
  extraItemId?: string;
  basicAttack: BasicAttackStyle;
  skillIds: string[];
  quickSlots: string[];
  apStart?: JobApStart;
}

export let DEFAULT_JOB_ID = seed.DEFAULT_JOB_ID as JobId;
export const JOB_ORDER: JobId[] = [...(seed.JOB_ORDER as JobId[])];
export const JOBS: Record<string, JobDef> = structuredClone(seed.JOBS) as Record<string, JobDef>;

export function getJob(id: string | undefined): JobDef {
  return JOBS[id ?? DEFAULT_JOB_ID] ?? JOBS[DEFAULT_JOB_ID];
}

export function isFirstJob(id: string | undefined): boolean {
  const job = getJob(id);
  return job.rank === 1 && JOB_ORDER.includes(job.id);
}

/** 1차 직업의 시작 AP. 2차 이후는 라인을 거슬러 올라간다. */
export function jobApStart(jobId?: string): JobApStart {
  let job = getJob(jobId);
  const seen = new Set<string>();
  while (job.prevId && !seen.has(job.id)) {
    seen.add(job.id);
    job = getJob(job.prevId);
  }
  return job.apStart ?? getJob(DEFAULT_JOB_ID).apStart ?? { str: 4, dex: 4, int: 4, luk: 4 };
}

export function jobLineRoot(jobId?: string): JobDef {
  let job = getJob(jobId);
  const seen = new Set<string>();
  while (job.prevId && !seen.has(job.id)) {
    seen.add(job.id);
    job = getJob(job.prevId);
  }
  return job;
}

/** 현재 직업이 해당 스킬(스킬의 jobId)을 배울 수 있는지. 같은 라인·같거나 낮은 차수. */
export function jobOwnsSkill(currentJobId: string | undefined, skillJobId: string): boolean {
  const current = getJob(currentJobId);
  const owner = getJob(skillJobId);
  if (owner.line !== current.line) return false;
  return owner.rank <= current.rank;
}

/** 현재 캐릭터 라인에서 해당 차수 직업. 아직 그 차수가 아니면 undefined. */
export function jobOnLineAtRank(currentJobId: string | undefined, rank: JobRank): JobDef | undefined {
  const chain: JobDef[] = [];
  let job = getJob(currentJobId);
  const seen = new Set<string>();
  while (!seen.has(job.id)) {
    seen.add(job.id);
    chain.push(job);
    if (!job.prevId) break;
    job = getJob(job.prevId);
  }
  chain.reverse();
  return chain.find((entry) => entry.rank === rank);
}

export function applyJobs(raw: unknown): void {
  const data = requireRecord(raw, 'jobs.json');
  if (typeof data.DEFAULT_JOB_ID === 'string') DEFAULT_JOB_ID = data.DEFAULT_JOB_ID;
  if (Array.isArray(data.JOB_ORDER)) replaceArray(JOB_ORDER, data.JOB_ORDER as JobId[]);
  replaceRecord(JOBS, structuredClone(requireRecord(data.JOBS, 'JOBS')) as Record<string, JobDef>);
}

if (import.meta.hot) {
  import.meta.hot.accept('./jobs.json', (mod) => {
    if (!mod?.default) return;
    applyJobs(mod.default);
    emitDataReloadSoon();
  });
}
